/**
 * The two-way message contract between the "Create slides" Office Dialog and
 * the task pane that opened it.
 *
 * WHY THIS EXISTS NOW
 * The freehand path only ever needed one message: the dialog collected the
 * wizard input and posted the finished config back on "Generate". The
 * templates path can't work that way — step 4 ("pick a layout for each
 * slide") has to SHOW the AI's outline and the template library, and neither
 * is reachable from the dialog window: it has no React tree of the pane's, no
 * bearer token, and no `PowerPoint.run()`. So the dialog stays open and asks
 * the pane to do the two things only the pane can do:
 *
 *   outline-request   pane calls POST /create-slide-outline (authorised) and
 *                     replies with the whole outline + per-slide template
 *                     rankings.
 *   catalog-request   pane mints a directory SAS per registered template
 *                     library and replies with the tokens; the dialog then
 *                     fetches each manifest itself over the network (a SAS
 *                     query string is self-contained auth), exactly as the
 *                     Asset Library's slide-template picker does.
 *
 * "generate" is still terminal: the pane closes the window and runs the fit +
 * assemble + insert pass so the user watches per-slide progress in the pane
 * with PowerPoint fully usable.
 *
 * Office.js gives exactly two channels, both size-capped and both carrying a
 * single string:
 *
 *   dialog -> pane   Office.context.ui.messageParent(string)
 *   pane   -> dialog Dialog.messageChild(string)   [DialogApi 1.2+]
 *
 * Both parsers treat what they receive as untrusted — a window and a pane can
 * be running different add-in builds. Mirrors asset-library/services/
 * assetLibraryDialogChannel.ts, which the create-slides flow deliberately
 * does not share: the two windows have disjoint message vocabularies and
 * couple to different features.
 */

import { logError, scrubMessage } from "../../../../../telemetry";
import type { PostedCreateSlideConfig, TemplatePlan } from "../hooks/types";
import type { OutlineResponse } from "./templatesApi";

const CREATE_SLIDES_CONNECTOR = "deckorate";

/** Shown when the pane reports a failure without a usable message. */
export const FALLBACK_OUTLINE_ERROR = "Couldn't generate the slide outline.";
export const FALLBACK_CATALOG_ERROR = "Couldn't load the slide template library.";

/** One library's directory SAS plus the manifest path inside it. */
export interface TemplateLibrarySas {
  readonly sasUrl: string;
  readonly manifestBlobPath: string;
}

/** Sent by the dialog window to the task pane. */
export type CreateSlidesDialogToPane =
  /** Step 3 -> 4: generate the outline and rank a template per slide. */
  | { readonly type: "outline-request"; readonly config: PostedCreateSlideConfig }
  /** The layout gallery needs the template library; asks for a SAS token. */
  | { readonly type: "catalog-request" }
  /**
   * Terminal. `plan` is present for the templates path (the approved
   * per-slide layout picks) and absent for freehand, where the config alone
   * drives generation.
   */
  | {
      readonly type: "generate";
      readonly config: PostedCreateSlideConfig;
      readonly plan?: TemplatePlan;
    }
  /** Terminal — the user closed or cancelled the wizard. */
  | { readonly type: "cancel" };

/** Sent by the task pane to the dialog window. */
export type CreateSlidesPaneToDialog =
  | { readonly type: "outline-ready"; readonly outline: OutlineResponse }
  | { readonly type: "outline-error"; readonly message: string }
  /**
   * A directory SAS per registered template library; the dialog fetches each
   * manifest itself with them. Plural because the outline ranker scores over
   * more than one folder (standard-layouts + transform-layouts) and blob-sas
   * mints one folder per call - see service/templateCatalogApi.ts.
   */
  | { readonly type: "catalog-ready"; readonly libraries: readonly TemplateLibrarySas[] }
  | { readonly type: "catalog-error"; readonly message: string };

/** Extracts the JSON string payload from either channel's event argument. */
function payloadOf(arg: unknown): string | null {
  const message = (arg as { message?: unknown } | null)?.message;
  return typeof message === "string" ? message : null;
}

function tryParseJson(raw: string, reason: string): unknown {
  try {
    return JSON.parse(raw);
  } catch (error) {
    // A malformed payload here means a mismatched dialog/pane build or a
    // corrupted channel message, not user input — worth tracing.
    logError(
      "error.connector.failure",
      {
        type: error instanceof Error ? error.name : "CreateSlidesChannelParseError",
        scrubbedMessage: scrubMessage(error instanceof Error ? error.message : String(error)),
      },
      { connectorSystemName: CREATE_SLIDES_CONNECTOR, reason },
    );
    return undefined;
  }
}

function toNonEmptyString(raw: unknown): string | null {
  return typeof raw === "string" && raw.length > 0 ? raw : null;
}

function toStringWithFallback(raw: unknown, fallback: string): string {
  return toNonEmptyString(raw) ?? fallback;
}

/** Narrows an unknown value to a plain object, or null. */
function toObject(raw: unknown): Record<string, unknown> | null {
  return typeof raw === "object" && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null;
}

/**
 * Narrows a received config. The wizard's own validation already ran in the
 * dialog, so this only checks that the shape is usable at all — a config
 * missing `topic`/`documents` would otherwise reach the generator and fail
 * deep inside a request builder.
 */
function toPostedConfig(raw: unknown): PostedCreateSlideConfig | null {
  const config = toObject(raw);
  if (!config) return null;
  if (typeof config.topic !== "string") return null;
  if (!Array.isArray(config.documents)) return null;
  return config as unknown as PostedCreateSlideConfig;
}

/** Narrows a received template plan; null when it carries no usable slides. */
function toTemplatePlan(raw: unknown): TemplatePlan | null {
  const plan = toObject(raw);
  if (!plan || !Array.isArray(plan.slides) || plan.slides.length === 0) return null;
  const slides = plan.slides.filter((slide) => {
    const s = toObject(slide);
    return s !== null && typeof s.template_id === "string" && s.template_id.length > 0;
  });
  if (slides.length === 0) return null;
  return { ...plan, slides } as unknown as TemplatePlan;
}

/** Parses a message received by the PANE. Returns null if it isn't one of ours. */
export function parseCreateSlidesDialogMessage(arg: unknown): CreateSlidesDialogToPane | null {
  const raw = payloadOf(arg);
  if (raw === null) return null;
  const parsed = tryParseJson(raw, "create-slides-pane-message-parse-failed");
  if (parsed === undefined) return null;

  const message = toObject(parsed);
  switch (message?.type) {
    case "catalog-request":
      return { type: "catalog-request" };
    case "cancel":
      return { type: "cancel" };
    case "outline-request": {
      const config = toPostedConfig(message.config);
      return config ? { type: "outline-request", config } : null;
    }
    case "generate": {
      const config = toPostedConfig(message.config);
      if (!config) return null;
      // A templates-path "generate" whose plan didn't survive the wire must
      // NOT silently fall through to freehand generation — that would produce
      // a completely different deck from the one the user approved.
      if (message.plan !== undefined) {
        const plan = toTemplatePlan(message.plan);
        return plan ? { type: "generate", config, plan } : null;
      }
      return { type: "generate", config };
    }
    default:
      return null;
  }
}

/** Parses a message received by the DIALOG. Returns null if it isn't one of ours. */
export function parseCreateSlidesPaneMessage(arg: unknown): CreateSlidesPaneToDialog | null {
  const raw = payloadOf(arg);
  if (raw === null) return null;
  const parsed = tryParseJson(raw, "create-slides-dialog-message-parse-failed");
  if (parsed === undefined) return null;

  const message = toObject(parsed);
  switch (message?.type) {
    case "outline-ready": {
      const outline = toObject(message.outline);
      if (!outline || !Array.isArray(outline.slides)) return null;
      return { type: "outline-ready", outline: outline as unknown as OutlineResponse };
    }
    case "outline-error":
      return { type: "outline-error", message: toStringWithFallback(message.message, FALLBACK_OUTLINE_ERROR) };
    case "catalog-ready": {
      if (!Array.isArray(message.libraries)) return null;
      const libraries: TemplateLibrarySas[] = [];
      for (const raw of message.libraries) {
        const library = toObject(raw);
        const sasUrl = toNonEmptyString(library?.sasUrl);
        const manifestBlobPath = toNonEmptyString(library?.manifestBlobPath);
        if (sasUrl && manifestBlobPath) libraries.push({ sasUrl, manifestBlobPath });
      }
      // No usable library is indistinguishable from a failure, so it must not
      // arrive as a "ready" the dialog then renders an empty gallery from.
      return libraries.length > 0 ? { type: "catalog-ready", libraries } : null;
    }
    case "catalog-error":
      return { type: "catalog-error", message: toStringWithFallback(message.message, FALLBACK_CATALOG_ERROR) };
    default:
      return null;
  }
}

export function serialiseCreateSlidesMessage(
  message: CreateSlidesDialogToPane | CreateSlidesPaneToDialog,
): string {
  return JSON.stringify(message);
}
